<template>
  <div>
    <PrmIconField>
      <PrmTextarea
        ref="inputHTML"
        :model-value="modelValue"
        :disabled="loading || disabled"
        :readonly="loading || readonly"
        :invalid="invalid"
        :placeholder="placeholder"
        :rows="rows"
        :auto-resize="autoResize"
        style="width: 100%"
        :class="{
          'text-monospaced': monospaced,
        }"
        @update:model-value="emit('update:modelValue', $event)"
        @change="emit('change', (($event as Event).target as HTMLInputElement).value)"
      />
      <PrmInputIcon
        v-if="loading"
        class="pi pi-spin pi-spinner"
      />
    </PrmIconField>
    <span
      v-if="!!errorMessage"
      class="input-wrapper-error"
    >
      {{ errorMessage }}
    </span>
  </div>
</template>

<script lang="ts" setup>
import PrmIconField from '@NitrexGroup/primevue/iconfield';
import PrmInputIcon from '@NitrexGroup/primevue/inputicon';
import PrmTextarea from '@NitrexGroup/primevue/textarea';
import {
  useTemplateRef,
} from 'vue';

defineProps<{
  modelValue?: string | null,
  loading?: boolean,
  // is for convenience
  disabled?: boolean,
  // is for convenience
  invalid?: boolean,
  // is for convenience
  readonly?: boolean,
  placeholder?: string,
  errorMessage?: string,
  /**
   * The number of visible text lines for the control. If it is specified, it must
   * be a positive integer.
   *
   * See: https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/textarea#rows
   *
   * @defaultValue 2
   */
  rows?: number,
  /**
   * When present, height of textarea changes as being typed.
   * @defaultValue false
   */
  autoResize?: boolean,
  /**
   * When set to true, a monospaced font will be used to display the text.
   */
  monospaced?: boolean,
}>();

const emit = defineEmits<{
  // fires on every key stroke
  (e: 'update:modelValue', value: string): void,
  // Fires when the value was changed. To understand when a value is considered,
  // changed, see:
  // https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/change_event#text_input_element
  (e: 'change', value: string | null | undefined): void,
}>();

// required to gain access to the actual HTML element, this is used to insert
// text at the cursor
const inputHTML = useTemplateRef('inputHTML');

defineExpose({
  inputHTML,
});

</script>
